import { useState, useEffect } from 'react';
import { BarChart3, Briefcase, FileText, Video, CheckCircle, ArrowRight, Activity } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import TrendIndicator from '../../components/ui/TrendIndicator';
import MiniSparkline from '../../components/ui/MiniSparkline';
import { useAuthStore } from '../../stores/authStore';
import { requiresOrganizationOnboarding } from '../../lib/onboarding';

export default function CorporateAnalytics() {
  const { user } = useAuthStore();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Mock trend data
  const campaignsTrend = [6, 7, 7, 8, 8, 9, 9];
  const invitationsTrend = [15, 18, 22, 25, 28, 32, 35];
  const scriptsTrend = [8, 10, 12, 14, 16, 18, 20];
  const submissionsTrend = [5, 6, 8, 10, 12, 14, 16];

  useEffect(() => {
    if (user && requiresOrganizationOnboarding(user)) {
      setLoading(false);
      setData(null);
      setError('Complete organization setup to unlock analytics.');
      return;
    }
    loadAnalytics();
  }, [user]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/analytics/brand');
      setData(response.data);
    } catch (err: any) {
      console.error('Load analytics error:', err);
      setData(null);
      if (err.response?.status === 403) {
        setError('You need a brand organization membership before analytics is available.');
      } else {
        setError(err.response?.data?.error || 'Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="space-y-6">
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    </div>;
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
          {error || 'No analytics available yet.'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-purple-600" />
            Campaign Analytics
          </h1>
          <p className="text-sm text-gray-600 mt-1">Track your campaign performance and ROI</p>
        </div>
        <button className="px-4 py-2 text-sm font-semibold text-purple-600 hover:bg-purple-50 rounded-lg transition-colors inline-flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Export Report
        </button>
      </div>

      {/* Grouped KPI Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Card 1: Campaigns & Invitations */}
        <Card className="shadow-lg hover:shadow-xl transition-shadow">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:divide-x divide-gray-200">
              
              {/* Total Campaigns */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Briefcase className="w-5 h-5 text-purple-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Campaigns</p>
                  </div>
                  <TrendIndicator value={22.5} />
                </div>
                
                <div className="flex items-baseline gap-2">
                  <p className="text-5xl font-extrabold text-gray-900">{data.jobs.total}</p>
                  <p className="text-lg text-gray-500 font-medium">total</p>
                </div>
                
                <p className="text-sm text-purple-600 font-semibold mt-2">
                  {data.jobs.byStatus['ACTIVE'] || 0} active now
                </p>
                
                <div className="mt-4">
                  <MiniSparkline data={campaignsTrend} color="#9333EA" height={40} />
                  <p className="text-xs text-gray-500 mt-2">Last 7 days trend</p>
                </div>
                
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-xs text-gray-600">{data.jobs.byStatus['DRAFT'] || 0} drafts</span>
                  <span className="text-xs text-purple-600 font-medium cursor-pointer hover:underline inline-flex items-center gap-1">
                    View all <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
              
              {/* Invitations */}
              <div className="sm:pl-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Invitations</p>
                  </div>
                  <TrendIndicator value={28.3} />
                </div>
                
                <div className="flex items-baseline gap-2">
                  <p className="text-5xl font-extrabold text-gray-900">{data.invitations.total}</p>
                  <p className="text-lg text-gray-500 font-medium">sent</p>
                </div>
                
                <p className="text-sm text-green-600 font-semibold mt-2">
                  {data.invitations.byStatus['ACCEPTED'] || 0} accepted
                </p>
                
                <div className="mt-4">
                  <MiniSparkline data={invitationsTrend} color="#10B981" height={40} />
                  <p className="text-xs text-gray-500 mt-2">Last 7 days trend</p>
                </div>
                
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-xs text-gray-600">{data.invitations.byStatus['PENDING'] || 0} pending</span>
                  <span className="text-xs text-green-600 font-medium cursor-pointer hover:underline inline-flex items-center gap-1">
                    Details <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
              
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Scripts & Submissions */}
        <Card className="shadow-lg hover:shadow-xl transition-shadow">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:divide-x divide-gray-200">
              
              {/* Scripts */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Scripts</p>
                  </div>
                  <TrendIndicator value={18.7} />
                </div>
                
                <div className="flex items-baseline gap-2">
                  <p className="text-5xl font-extrabold text-gray-900">{data.scripts.total}</p>
                  <p className="text-lg text-gray-500 font-medium">total</p>
                </div>
                
                <p className="text-sm text-blue-600 font-semibold mt-2">
                  {data.scripts.byStatus['APPROVED_BY_BRAND'] || 0} approved
                </p>
                
                <div className="mt-4">
                  <MiniSparkline data={scriptsTrend} color="#3B82F6" height={40} />
                  <p className="text-xs text-gray-500 mt-2">Last 7 days trend</p>
                </div>
                
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-xs text-gray-600">{data.scripts.byStatus['SUBMITTED_FOR_BRAND'] || 0} pending</span>
                  <span className="text-xs text-blue-600 font-medium cursor-pointer hover:underline inline-flex items-center gap-1">
                    Review <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
              
              {/* Submissions */}
              <div className="sm:pl-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Video className="w-5 h-5 text-orange-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Submissions</p>
                  </div>
                  <TrendIndicator value={32.1} />
                </div>
                
                <div className="flex items-baseline gap-2">
                  <p className="text-5xl font-extrabold text-gray-900">{data.submissions.total}</p>
                  <p className="text-lg text-gray-500 font-medium">total</p>
                </div>
                
                <p className="text-sm text-orange-600 font-semibold mt-2">
                  {data.submissions.byStatus['APPROVED'] || 0} approved
                </p>
                
                <div className="mt-4">
                  <MiniSparkline data={submissionsTrend} color="#F59E0B" height={40} />
                  <p className="text-xs text-gray-500 mt-2">Last 7 days trend</p>
                </div>
                
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-xs text-gray-600">{data.submissions.byStatus['SUBMITTED'] || 0} pending</span>
                  <span className="text-xs text-orange-600 font-medium cursor-pointer hover:underline inline-flex items-center gap-1">
                    Review <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
              
            </div>
          </CardContent>
        </Card>
        
      </div>

      {/* Campaign Performance */}
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-bold">Campaign Performance</CardTitle>
            <TrendIndicator value={15.8} label="vs last month" />
          </div>
        </CardHeader>
        <CardContent>
          {data.jobPerformance.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-bold text-gray-700">Campaign</th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-gray-700">Status</th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-gray-700">Invitations</th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-gray-700">Acceptance</th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-gray-700">Scripts</th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-gray-700">Videos</th>
                  </tr>
                </thead>
                <tbody>
                  {data.jobPerformance.map((job: any) => (
                    <tr key={job.jobId} className="border-b hover:bg-purple-50 transition-colors">
                      <td className="py-4 px-4 text-sm font-semibold text-gray-900">{job.jobTitle}</td>
                      <td className="py-4 px-4 text-center">
                        <Badge variant={job.status === 'ACTIVE' ? 'success' : 'default'}>
                          {job.status}
                        </Badge>
                      </td>
                      <td className="py-4 px-4 text-center text-sm font-bold text-gray-900">{job.invitations}</td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-sm font-bold text-green-600">
                          {job.acceptanceRate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center text-sm font-bold text-gray-900">{job.scripts}</td>
                      <td className="py-4 px-4 text-center text-sm font-bold text-gray-900">{job.submissions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No campaigns yet</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Pending Scripts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.recentActivity.scripts.length > 0 ? (
              <div className="space-y-3">
                {data.recentActivity.scripts.map((script: any) => (
                  <div key={script.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{script.kolName}</p>
                      <p className="text-xs text-gray-600">{script.jobTitle}</p>
                    </div>
                    <Badge variant="warning">Needs Review</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No pending scripts</p>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Video className="w-5 h-5 text-orange-600" />
              Pending Videos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.recentActivity.submissions.length > 0 ? (
              <div className="space-y-3">
                {data.recentActivity.submissions.map((sub: any) => (
                  <div key={sub.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{sub.kolName}</p>
                      <p className="text-xs text-gray-600">{sub.jobTitle}</p>
                    </div>
                    <Badge variant="warning">Needs Review</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No pending videos</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
